import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Form, Button, Alert, Spinner, Modal, InputGroup } from 'react-bootstrap';
import { fetchUserProfile, updateUserProfile, fetchCourses } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ProfilePage = () => {
  const { user: authUser } = useAuth();
  const userId = authUser?.id;
  const [user, setUser] = useState(null);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // 1. Fetch Profile Data & Course List on Mount
  useEffect(() => {
    const loadProfileData = async () => {
      try {
        setLoading(true);
        const [userData, coursesData] = await Promise.all([
          fetchUserProfile(userId),
          fetchCourses()
        ]);
        
        setUser({
          ...userData,
          course_credentials: userData.course_credentials || []
        });
        setAvailableCourses(coursesData || []);
      } catch (err) {
        console.error('Error loading profile page data:', err);
        setMessage({ type: 'danger', text: 'Failed to load profile data.' });
      } finally {
        setLoading(false);
      }
    };

    if (userId) loadProfileData();
  }, [userId]);

  // 2. Handle Inputs for Main Profile
  const handleProfileChange = (field, value) => {
    setUser(prev => ({ ...prev, [field]: value }));
  };

  // 3. Handle Inputs for Course Credentials
  const handleCredentialChange = (index, field, value) => {
    setUser(prevUser => {
      const credentials = [...(prevUser.course_credentials || [])];
      
      if (field === 'course_id') {
        const selectedCourse = availableCourses.find(c => c.id === parseInt(value, 10));
        credentials[index] = { 
          ...credentials[index], 
          course_id: value ? parseInt(value, 10) : '',
          course_name: selectedCourse ? selectedCourse.name : '',
          platform: selectedCourse ? (selectedCourse.platform || 'webtrac') : 'webtrac' 
        };
      } else {
        credentials[index] = { ...credentials[index], [field]: value };
      }

      return { ...prevUser, course_credentials: credentials };
    });
  };

  const handleAddCourseLogin = () => {
    setUser(prevUser => ({
      ...prevUser,
      course_credentials: [
        ...(prevUser.course_credentials || []),
        { course_id: '', course_name: '', platform: 'webtrac', username: '', password: '' }
      ]
    }));
  };

  const handleRemoveCourseLogin = (index) => {
    setUser(prevUser => {
      const credentials = [...(prevUser.course_credentials || [])];
      credentials.splice(index, 1);
      return { ...prevUser, course_credentials: credentials };
    });
  };

  // 4. Save Changes
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const updatedUser = await updateUserProfile(userId, user);
      setUser({
        ...updatedUser,
        course_credentials: updatedUser.course_credentials || []
      });
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      console.error('Error updating profile:', err);
      setMessage({ type: 'danger', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading profile...</p>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="py-4" style={{ maxWidth: '900px' }}>
        <Alert variant="danger">{message?.text || 'Unable to load profile.'}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ maxWidth: '900px' }}>
      {message && <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Form onSubmit={handleSave}>
        <h2 className="fw-bold mb-4">User Profile</h2>

        {/* SECTION 1: Personal Info */}
        <Card className="shadow-sm border-0 rounded-3 mb-4">
          <Card.Header className="bg-white border-bottom fw-bold d-flex justify-content-between align-items-center py-3 fs-5">
            Golfer Info
            {!isEditing && (
              <Button variant="outline-primary" size="sm" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </Card.Header>
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-muted mb-1">First Name</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      value={user.first_name || ''}
                      onChange={e => handleProfileChange('first_name', e.target.value)}
                    />
                  ) : (
                    <div className="form-control-static py-2 px-3 border rounded bg-light">{user.first_name || '—'}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-muted mb-1">Last Name</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      value={user.last_name || ''}
                      onChange={e => handleProfileChange('last_name', e.target.value)}
                    />
                  ) : (
                    <div className="form-control-static py-2 px-3 border rounded bg-light">{user.last_name || '—'}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="text-muted mb-1">Address</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      value={user.street_address || ''}
                      onChange={e => handleProfileChange('street_address', e.target.value)}
                    />
                  ) : (
                    <div className="form-control-static py-2 px-3 border rounded bg-light">{user.street_address || '—'}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="text-muted mb-1">City</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      value={user.city || ''}
                      onChange={e => handleProfileChange('city', e.target.value)}
                    />
                  ) : (
                    <div className="form-control-static py-2 px-3 border rounded bg-light">{user.city || '—'}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="text-muted mb-1">State</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      value={user.state || ''}
                      onChange={e => handleProfileChange('state', e.target.value)}
                    />
                  ) : (
                    <div className="form-control-static py-2 px-3 border rounded bg-light">{user.state || '—'}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-muted mb-1">Zip Code</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      value={user.zip || ''}
                      onChange={e => handleProfileChange('zip', e.target.value)}
                    />
                  ) : (
                    <div className="form-control-static py-2 px-3 border rounded bg-light">{user.zip || '—'}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-muted mb-1">Phone</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      value={user.phone || ''}
                      onChange={e => handleProfileChange('phone', e.target.value)}
                    />
                  ) : (
                    <div className="form-control-static py-2 px-3 border rounded bg-light">{user.phone || '—'}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-muted mb-1">Email</Form.Label>
                  {isEditing ? (
                    <Form.Control
                      type="email"
                      value={user.email || ''}
                      onChange={e => handleProfileChange('email', e.target.value)}
                    />
                  ) : (
                    <div className="form-control-static py-2 px-3 border rounded bg-light">{user.email || '—'}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-muted mb-1">Password</Form.Label>
                  <InputGroup>
                    <Form.Control type="password" value="••••••••" readOnly disabled />
                    <Button variant="outline-secondary" onClick={() => setShowPasswordModal(true)}>
                      Change
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* SECTION 2: Course Credentials */}
        <Card className="shadow-sm border-0 rounded-3 mb-4">
          <Card.Header className="bg-white border-bottom fw-bold d-flex justify-content-between align-items-center py-3 fs-5">
            <span>Course Credentials</span>
            {!isEditing && (
              <Button variant="outline-primary" size="sm" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </Card.Header>
          <Card.Body className="p-4">
            {(user.course_credentials || []).length === 0 ? (
              <p className="text-muted fst-italic mb-0">No course credentials saved yet.</p>
            ) : (
              user.course_credentials.map((cred, idx) => (
                <Row key={idx} className="mb-3 align-items-end border-bottom pb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="text-muted fw-normal mb-1">Golf Course</Form.Label>
                      {isEditing ? (
                        <Form.Select 
                          value={cred.course_id || ''} 
                          onChange={(e) => handleCredentialChange(idx, 'course_id', e.target.value)}
                        >
                          <option value="">Select a Course...</option>
                          {availableCourses.map(course => (
                            <option key={course.id} value={course.id}>
                              {course.name} ({course.platform || 'WebTrac'})
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <div className="form-control-static py-2 px-3 border rounded bg-light fw-bold">
                          {cred.course_name || 'Selected Course'}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="text-muted fw-normal mb-1">Username</Form.Label>
                      {isEditing ? (
                        <Form.Control 
                          type="text"
                          value={cred.username || ''} 
                          onChange={(e) => handleCredentialChange(idx, 'username', e.target.value)} 
                          placeholder="Username"
                        />
                      ) : (
                        <div className="form-control-static py-2 px-3 border rounded bg-light">
                          {cred.username || '—'}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="text-muted fw-normal mb-1">Password</Form.Label>
                      {isEditing ? (
                        <Form.Control 
                          type="password"
                          value={cred.password || ''} 
                          onChange={(e) => handleCredentialChange(idx, 'password', e.target.value)} 
                          placeholder="Password"
                        />
                      ) : (
                        <div className="form-control-static py-2 px-3 border rounded bg-light">
                          {cred.password ? '••••••••' : '—'}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  {isEditing && (
                    <Col md={1} className="text-end">
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={() => handleRemoveCourseLogin(idx)}
                        title="Remove Login"
                      >
                        ✕
                      </Button>
                    </Col>
                  )}
                </Row>
              ))
            )}

            {isEditing && (
              <Button
                variant="outline-primary"
                onClick={handleAddCourseLogin}
                className="w-100 d-flex align-items-center justify-content-center py-3 mt-1"
              >
                <span className="fs-2 lh-1">+</span>
              </Button>
            )}
          </Card.Body>
        </Card>

        {isEditing && (
          <div className="d-flex justify-content-end">
            <Button
              variant="secondary"
              className="me-2"
              onClick={() => setIsEditing(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </Form>

      {/* Password Change Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This feature is coming soon! For now, password changes must be handled by an administrator.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProfilePage;